<template>
    <DialogBox :visible="visible" header="Download RM Data" dialogWidth="35rem" @handleClose="handleClose">
        <template #dialogContent>
            <div class="download-card">
                <div class="download-card-header">
                    <h3>Select Insurer to Download</h3>
                </div>

                <div class="insurer-selector">
                    <label for="insurer-select">Please select Insurer: <span class="required-asterisk">*</span></label>
                    <Select id="insurer-select" v-model="selectedInsurer" :options="insurerOptions" 
                        optionLabel="name" optionValue="value" placeholder="Select Insurer" 
                        :filter="insurerOptions.length > 7" filterPlaceholder="Search..." 
                        :class="['insurer-select-dropdown', 'w-100', { 'p-invalid': insurerError }]" />
                    <small v-if="insurerError" class="p-error">Insurer selection is required</small>
                </div>

                <div class="download-actions">
                    <Button label="Download" icon="pi pi-download" :loading="downloading" 
                        :disabled="!selectedInsurer || downloading" 
                        @click="handleDownload" class="download-button" />
                </div>

                <div class="download-status">
                    <p v-if="errorDisplayMessage" class="error-text">{{ errorDisplayMessage }}</p>
                </div>
            </div>
        </template>
    </DialogBox>
</template>
<script>
import DialogBox from '@/components/common/DialogBox.vue';
import { Select, Button } from 'primevue';

export default {
    name: 'RMDownloadDialog',
    components: {
        DialogBox,
        Select,
        Button
    },
    props: {
        visible: {
            type: Boolean,
            required: true
        }
    },
    emits: ['close'],
    data() {
        return {
            errorDisplayMessage: '',
            downloading: false,
            selectedInsurer: null,
            insurerError: false,
            insurerOptions: [],
        };
    },
    watch: {
        visible(val) {
            if (!val) {
                this.resetState();
            } else {
                // Update options when dialog opens
                this.updateInsurerOptions();
            }
        },
        selectedInsurer(newVal) {
            if (newVal) {
                this.insurerError = false;
            }
        },
        '$store.state.configData': {
            handler() {
                this.updateInsurerOptions();
            },
            deep: true,
            immediate: true
        }
    },
    mounted() {
        this.updateInsurerOptions();
    },
    methods: {
        updateInsurerOptions() {
            const configData = this.$store.state.configData;
            if (!configData || !configData.INSURER_LIST) {
                this.insurerOptions = [];
                return;
            }
            const insurerList = Array.isArray(configData.INSURER_LIST) ? configData.INSURER_LIST : [];
            this.insurerOptions = insurerList
                .map(insurer => ({
                    name: insurer.manufacturer_name || insurer.name,
                    value: insurer.manufacturer_id || insurer.value
                }))
                .filter(insurer => insurer.name && insurer.value)
                .sort((a, b) =>
                    String(a.name || '').localeCompare(String(b.name || ''), undefined, {
                        sensitivity: 'base'
                    })
                );
        },
        handleClose() {
            this.resetState();
            this.$emit('close');
        },
        resetState() {
            this.errorDisplayMessage = '';
            this.downloading = false;
            this.selectedInsurer = null;
            this.insurerError = false;
        },
        async handleDownload() {
            if (!this.selectedInsurer) {
                this.insurerError = true;
                this.errorDisplayMessage = 'Please select an insurer first.';
                return;
            }
            this.insurerError = false;
            this.errorDisplayMessage = '';

            this.downloading = true;
            const showError = (message = 'Failed to download') => {
                if (this.$toast) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Download Failed',
                        detail: message,
                        life: 4000
                    });
                } else {
                    alert(message);
                }
            };

            try {
                const response = await this.backendApi('fileDownload', { param1: this.selectedInsurer }, {}, 'get');
                
                if (!response?.data) {
                    showError();
                } else if (response.data.errors?.length) {
                    showError(response.data.errors[0].errorDisplayMessage);
                } else if (response.data.data?.rm_data) {
                    const base64Csv = response.data.data.rm_data;
                    // Get insurer name from selected insurer
                    const selectedInsurerOption = this.insurerOptions.find(insurer => insurer.value === this.selectedInsurer);
                    const insurerName = selectedInsurerOption?.name || 'rm_data';
                    // Get file extension from API response, default to 'xlsx' if not provided
                    const fileExtension = response.data.data.file_extension || 'xlsx';
                    // Sanitize filename: remove invalid filename characters (keep spaces, letters, numbers, hyphens, underscores)
                    const sanitizedFileName = insurerName.replace(/[<>:"/\\|?*]/g, '_').trim() + '.' + fileExtension;
                    const fileName = sanitizedFileName;
                    const link = document.createElement('a');
                    link.href = base64Csv;
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    
                    // Show success toast
                    if (this.$toast) {
                        this.$toast.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: 'File downloaded successfully',
                            life: 3000
                        });
                    }
                    
                    // Close the dialog after a short delay
                    setTimeout(() => {
                        this.handleClose();
                    }, 500);
                } else if (response.data.data?.url) {
                    // Get insurer name from selected insurer
                    const selectedInsurerOption = this.insurerOptions.find(insurer => insurer.value === this.selectedInsurer);
                    const insurerName = selectedInsurerOption?.name || 'rm_data';
                    // Get file extension from API response, default to 'xlsx' if not provided
                    const fileExtension = response.data.data.file_extension || 'xlsx';
                    // Sanitize filename: remove invalid filename characters (keep spaces, letters, numbers, hyphens, underscores)
                    const sanitizedFileName = insurerName.replace(/[<>:"/\\|?*]/g, '_').trim() + '.' + fileExtension;
                    
                    // For URL downloads, trigger download with custom filename
                    const link = document.createElement('a');
                    link.href = response.data.data.url;
                    link.download = sanitizedFileName;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    
                    // Show success toast
                    if (this.$toast) {
                        this.$toast.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: 'File downloaded successfully',
                            life: 3000
                        });
                    }
                    
                    // Close the dialog after a short delay
                    setTimeout(() => {
                        this.handleClose();
                    }, 500);
                } else {
                    showError();
                }
            } catch (error) {
                console.error('Download error:', error);
                showError();
            } finally {
                this.downloading = false;
            }
        }
    }
}
</script>
<style scoped lang="scss">
@import '@/assets/scss/distributorVariables/variable.scss';

.download-card {
    .download-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 1rem;

        h3 {
            margin: 0;
            font-size: 1.25rem;
        }
    }

    .insurer-selector {
        margin-bottom: 1.5rem;

        label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 600;
            color: #000;

            .required-asterisk {
                color: #dc3545;
                margin-left: 0.25rem;
            }
        }

        .insurer-select-dropdown {
            width: 100%;
        }

        .p-error {
            display: block;
            margin-top: 0.25rem;
        }
    }

    .download-actions {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 1rem;

        .download-button {
            min-width: 120px;
        }
    }

    .download-status {
        margin-top: 1rem;

        .error-text {
            color: #dc3545;
            font-weight: 600;
        }
    }
}
</style>
